import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, FileDown, Plus, Trash2, Paperclip, Landmark, ShieldCheck, ArrowLeft } from "lucide-react";

const Kpi = ({ label, value, color }) => (
  <div className="panel p-3" data-testid={`vp-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-mono text-base font-bold" style={{ color }}>{value}</div>
  </div>
);

export default function VendorProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const isFull = ["admin", "contador"].includes(user?.role);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("compras");
  const [ba, setBa] = useState({ bank: "", clabe: "", holder: "" });
  const [exp, setExp] = useState({ legal_name: "", tax_regime: "", fiscal_address: "" });
  const [contact, setContact] = useState({ name: "", role: "", phone: "", email: "" });

  const load = useCallback(() => {
    api.get(`/vendors/${id}/profile`).then(({ data }) => {
      setData(data);
      setExp({ legal_name: data.vendor.legal_name || "", tax_regime: data.vendor.tax_regime || "", fiscal_address: data.vendor.fiscal_address || "" });
    }).catch(() => toast.error("No se pudo cargar el proveedor"));
  }, [id]);
  useEffect(load, [load]);

  if (!data) return <div className="text-center py-16"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  const v = data.vendor;

  const saveExpediente = async () => {
    try { await api.patch(`/vendors/${id}`, exp); toast.success("Expediente actualizado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const addContact = async () => {
    if (!contact.name) return toast.error("Nombre del contacto requerido");
    try { await api.patch(`/vendors/${id}`, { contacts: [...(v.contacts || []), { ...contact, id: crypto.randomUUID() }] }); setContact({ name: "", role: "", phone: "", email: "" }); toast.success("Contacto agregado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const delContact = async (cid) => {
    await api.patch(`/vendors/${id}`, { contacts: (v.contacts || []).filter((c) => c.id !== cid) }); load();
  };
  const addBank = async () => {
    if (!ba.bank) return toast.error("Banco requerido");
    try { await api.post(`/vendors/${id}/bank-accounts`, ba); setBa({ bank: "", clabe: "", holder: "" }); toast.success("Cuenta agregada (auditada)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const delBank = async (baId) => {
    if (!window.confirm("¿Eliminar esta cuenta bancaria? Quedará registrada la baja en el historial.")) return;
    try { await api.delete(`/vendors/${id}/bank-accounts/${baId}`); toast.success("Cuenta eliminada (auditada)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const uploadDoc = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/vendors/${id}`, { documents: [...(v.documents || []), { id: up.id, label: file.name, at: new Date().toISOString() }] });
      toast.success("Documento adjuntado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const downloadStatement = async () => {
    try {
      const res = await api.get(`/vendors/${id}/statement/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Estado_cuenta_${v.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF"); }
  };

  const TABS = [["compras", `Compras (${data.cargos.length})`], ["pagos", `Pagos (${data.pagos.length})`],
    ["cfdi", `CFDI (${data.sat_invoices.length})`], ["pendientes", `Sin factura (${data.pendientes_factura.length})`], ["expediente", "Expediente"]];

  return (
    <div data-testid="vendor-profile-page">
      <PageHeader title={v.name} subtitle={`Proveedor${v.rfc ? ` · ${v.rfc}` : ""}`} testId="vendor-profile-header"
        actions={<button onClick={downloadStatement} data-testid="vendor-statement-btn" className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2"><FileDown className="w-4 h-4" /> Estado de cuenta</button>} />
      <div className="px-6 lg:px-8 py-6 space-y-4">
        <Link to="/vendors" className="text-[11px] text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Directorio</Link>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Comprado" value={fmtMXN(data.total_comprado)} color="#22d3ee" />
          <Kpi label="Pagado" value={fmtMXN(data.total_pagado)} color="#86efac" />
          <Kpi label="Adeudo" value={fmtMXN(data.saldo)} color={data.saldo > 0 ? "#f59e0b" : "#86efac"} />
          <Kpi label="Condiciones" value={v.payment_terms_days ? `Crédito ${v.payment_terms_days}d` : "Contado"} color="var(--text)" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {TABS.map(([t, l]) => (
            <button key={t} data-testid={`vp-tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm border ${tab === t ? "text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}
              style={{ borderColor: tab === t ? "var(--cyan)" : "var(--border)", background: tab === t ? "rgba(34,211,238,0.07)" : "transparent" }}>{l}</button>
          ))}
        </div>

        {tab === "compras" && (
          <div className="space-y-1.5" data-testid="vp-compras">
            {data.purchase_orders.length > 0 && (
              <div className="text-[10px] font-mono text-slate-500">OCs: {data.purchase_orders.map((p) => `${p.folio} (${p.status}, ${fmtMXN(p.total)})`).join(" · ")}</div>
            )}
            {data.cargos.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">Sin compras registradas.</div>}
            {data.cargos.slice().reverse().map((c) => (
              <div key={c.id} className="panel px-4 py-2 flex items-center gap-3 flex-wrap text-xs">
                <span className="text-slate-500 font-mono">{fmtDate(c.date)}</span>
                <span className="flex-1 text-slate-300 truncate">{c.description || c.category}</span>
                {c.due_date && c.saldo > 0 && <span className="font-mono text-[10px]" style={{ color: c.due_date < new Date().toISOString().slice(0, 10) ? "#fca5a5" : "#f59e0b" }}>vence {fmtDate(c.due_date)}</span>}
                <span className="font-mono">{fmtMXN(c.amount)}</span>
                <span className="font-mono" style={{ color: c.saldo > 0 ? "#f59e0b" : "#86efac" }}>{c.saldo > 0 ? `debe ${fmtMXN(c.saldo)}` : "pagado"}</span>
                <span className="text-[9px] font-mono px-1 rounded-sm" style={{ border: "1px solid var(--border)", color: c.invoice_received ? "#86efac" : c.invoice_not_required ? "var(--text-dim)" : "#f59e0b" }}>
                  {c.invoice_received ? "CFDI ✓" : c.invoice_not_required ? "sin CFDI" : "CFDI pend."}
                </span>
              </div>
            ))}
          </div>
        )}
        {tab === "pagos" && (
          <div className="space-y-1.5" data-testid="vp-pagos">
            {data.pagos.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">Sin pagos de tesorería. Los pagos se registran en Finanzas → Tesorería y Bancos.</div>}
            {data.pagos.map((p) => (
              <div key={p.id} className="panel px-4 py-2 flex items-center gap-3 text-xs">
                <span className="font-mono text-slate-500">{p.folio}</span>
                <span className="text-slate-500">{fmtDate(p.date)}</span>
                <span className="flex-1 text-slate-300 truncate">{p.account_label}{p.bank_reference ? ` · ref ${p.bank_reference}` : ""}</span>
                <span className="font-mono font-bold" style={{ color: "#86efac" }}>{fmtMXN(p.amount)}</span>
              </div>
            ))}
          </div>
        )}
        {tab === "cfdi" && (
          <div className="space-y-1.5" data-testid="vp-cfdi">
            {data.sat_invoices.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">{v.rfc ? "Sin CFDI recibidos de este RFC." : "Registra el RFC del proveedor para ver sus CFDI."}</div>}
            {data.sat_invoices.map((i) => (
              <div key={i.id} className="panel px-4 py-2 flex items-center gap-3 text-xs flex-wrap">
                <span className="font-mono text-slate-500">{(i.uuid || "").slice(0, 8)}…</span>
                <span className="text-slate-500">{fmtDate(i.fecha)}</span>
                <span className="flex-1 text-slate-300 truncate">{i.nombre_emisor}</span>
                <span className="text-[9px] font-mono px-1 rounded-sm" style={{ border: "1px solid var(--border)", color: i.match_status === "matched" ? "#86efac" : "#f59e0b" }}>{i.match_status}</span>
                <span className="font-mono font-bold">{fmtMXN(i.total)}</span>
              </div>
            ))}
          </div>
        )}
        {tab === "pendientes" && (
          <div className="space-y-1.5" data-testid="vp-pendientes">
            {data.pendientes_factura.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">✓ Todos los gastos tienen comprobante.</div>}
            {data.pendientes_factura.map((c) => (
              <div key={c.id} className="panel px-4 py-2 flex items-center gap-3 text-xs">
                <span className="text-slate-500 font-mono">{fmtDate(c.date)}</span>
                <span className="flex-1 text-slate-300 truncate">{c.description || c.category}</span>
                <span className="font-mono" style={{ color: "#f59e0b" }}>{fmtMXN(c.amount)}</span>
                <span className="text-[10px]" style={{ color: "#f59e0b" }}>factura pendiente</span>
              </div>
            ))}
          </div>
        )}
        {tab === "expediente" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vp-expediente">
            <div className="panel p-4 space-y-2 text-xs">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">// Datos fiscales</div>
              <input className="input-cyber px-2 py-1.5 w-full" placeholder="Razón social" value={exp.legal_name} onChange={(e) => setExp({ ...exp, legal_name: e.target.value })} data-testid="vp-legal-name" />
              <input className="input-cyber px-2 py-1.5 w-full" placeholder="Régimen fiscal (ej. 601 General de Ley PM)" value={exp.tax_regime} onChange={(e) => setExp({ ...exp, tax_regime: e.target.value })} data-testid="vp-regime" />
              <textarea className="input-cyber px-2 py-1.5 w-full" rows={2} placeholder="Domicilio fiscal" value={exp.fiscal_address} onChange={(e) => setExp({ ...exp, fiscal_address: e.target.value })} data-testid="vp-fiscal-address" />
              <button onClick={saveExpediente} className="btn-primary px-3 py-1.5 text-[10px] uppercase tracking-widest" data-testid="vp-save-expediente">Guardar</button>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 pt-2">// Documentos y constancias</div>
              {(v.documents || []).map((d) => <div key={d.id} className="text-slate-300">📄 {d.label} <span className="text-slate-500">({fmtDate(d.at)})</span></div>)}
              <label className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest cursor-pointer inline-block" style={{ border: "1px solid var(--border)" }}>
                <Paperclip className="w-3 h-3 inline mr-1" /> Adjuntar constancia
                <input type="file" className="hidden" accept="image/*,application/pdf" data-testid="vp-doc-upload" onChange={(e) => { if (e.target.files[0]) uploadDoc(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            <div className="space-y-4">
              <div className="panel p-4 space-y-2 text-xs">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">// Contactos</div>
                {(v.contacts || []).map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-slate-300">
                    <span className="flex-1">{c.name}{c.role ? ` · ${c.role}` : ""}{c.phone ? ` · ${c.phone}` : ""}{c.email ? ` · ${c.email}` : ""}</span>
                    <button onClick={() => delContact(c.id)} className="btn-ghost p-1" style={{ color: "#fca5a5" }}><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-1.5">
                  <input className="input-cyber px-2 py-1" placeholder="Nombre" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="vp-contact-name" />
                  <input className="input-cyber px-2 py-1" placeholder="Puesto" value={contact.role} onChange={(e) => setContact({ ...contact, role: e.target.value })} />
                  <input className="input-cyber px-2 py-1" placeholder="Teléfono" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                  <input className="input-cyber px-2 py-1" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                </div>
                <button onClick={addContact} className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }} data-testid="vp-add-contact"><Plus className="w-3 h-3 inline" /> Contacto</button>
              </div>
              <div className="panel p-4 space-y-2 text-xs">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" style={{ color: "#86efac" }} /> Cuentas bancarias protegidas</div>
                {(v.bank_accounts || []).map((b) => (
                  <div key={b.id} className="flex items-center gap-2 text-slate-300" data-testid={`vp-bank-${b.id}`}>
                    <Landmark className="w-3 h-3 text-slate-500" />
                    <span className="flex-1">{b.bank} · <span className="font-mono">{b.clabe || b.account_number || "—"}</span>{b.holder ? ` · ${b.holder}` : ""}</span>
                    {isFull && <button onClick={() => delBank(b.id)} className="btn-ghost p-1" style={{ color: "#fca5a5" }} data-testid={`vp-bank-del-${b.id}`}><Trash2 className="w-3 h-3" /></button>}
                  </div>
                ))}
                {!isFull && <div className="text-[10px] text-slate-500">Las CLABE se muestran enmascaradas para tu rol.</div>}
                {isFull && (
                  <>
                    <div className="grid grid-cols-3 gap-1.5">
                      <input className="input-cyber px-2 py-1" placeholder="Banco" value={ba.bank} onChange={(e) => setBa({ ...ba, bank: e.target.value })} data-testid="vp-bank-name" />
                      <input className="input-cyber px-2 py-1" placeholder="CLABE (18 dígitos)" value={ba.clabe} onChange={(e) => setBa({ ...ba, clabe: e.target.value })} data-testid="vp-bank-clabe" />
                      <input className="input-cyber px-2 py-1" placeholder="Titular" value={ba.holder} onChange={(e) => setBa({ ...ba, holder: e.target.value })} data-testid="vp-bank-holder" />
                    </div>
                    <button onClick={addBank} className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }} data-testid="vp-bank-add"><Plus className="w-3 h-3 inline" /> Cuenta (auditada)</button>
                    {(v.bank_history || []).length > 0 && (
                      <div className="text-[10px] text-slate-500 pt-1">
                        Historial: {v.bank_history.slice(-3).map((h) => `${h.action} ${h.bank} ${h.clabe || ""} por ${h.by}`).join(" · ")}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
